import { BookOpen, ClipboardCheck, FileSignature, Gift, Home, Menu, Shield, Sparkles, X } from 'lucide-react';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSwipeable } from 'react-swipeable';

import { useReservation } from '@/contexts';
import { PortalItem, PortalItemKey } from '@/lib/api';
import { LanguageSelector } from '@/components/LanguageSelector';
import type { LoyaltyOutletState } from './PortalShellContainer';

const LOYALTY_SLUG = 'loyalty';

const ICON_FOR_KEY: Record<PortalItemKey, typeof BookOpen> = {
  rental_agreement: FileSignature,
  check_in_form: ClipboardCheck,
  damage_waiver: Shield,
  guidebook: BookOpen,
  upsells: Sparkles,
};

// Portal routes: nested under /portal/home/:section. See App.tsx for the routes.
// The key ('rental_agreement', etc.) maps to a slug used in the URL.
const SLUG_FOR_KEY: Record<PortalItemKey, string> = {
  rental_agreement: 'rental-agreement',
  damage_waiver: 'damage-waiver',
  check_in_form: 'check-in',
  guidebook: 'guidebook',
  upsells: 'add-ons',
};

interface PortalShellProps {
  items: PortalItem[];
  loyalty: LoyaltyOutletState;
  children: ReactNode;
}

function parseLocalDate(input: string | undefined | null): Date | null {
  if (!input) return null;
  const m = String(input).match(/^(\d{4}-\d{2}-\d{2})/);
  if (!m) return null;
  const [y, mo, d] = m[1].split('-').map(Number);
  return new Date(y, mo - 1, d);
}

function formatCompactDate(d: Date): string {
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' }).format(d);
}

function computeNights(a: Date, b: Date): number {
  return Math.max(1, Math.round((b.getTime() - a.getTime()) / (1000 * 60 * 60 * 24)));
}

export default function PortalShell({ items, loyalty, children }: PortalShellProps) {
  const { t } = useTranslation('portal');
  const navigate = useNavigate();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  const isHome = location.pathname === '/portal/home' || location.pathname === '/portal/home/';

  const onNavHome = () => navigate('/portal/home');
  const onNavItem = (key: PortalItemKey) => navigate(`/portal/home/${SLUG_FOR_KEY[key]}`);
  const onNavLoyalty = () => navigate('/portal/home/loyalty');

  const activeSlug = location.pathname.startsWith('/portal/home/')
    ? location.pathname.replace('/portal/home/', '').split('/')[0]
    : '';

  const activeItem = items.find((i) => SLUG_FOR_KEY[i.key] === activeSlug);
  const mobileTitle = isHome
    ? t('Overview')
    : activeSlug === LOYALTY_SLUG
      ? t('Loyalty program')
      : (activeItem?.title ?? t('Guest portal'));

  // ─── Swipe-to-open drawer (mobile) ────────────────────────────────────
  // Mirrors the Guidebook's left-edge swipe-to-go-back pattern. A thin
  // edge-grab strip on the left catches swipes that start from the edge;
  // partial drag reveals the drawer behind a dim overlay, past the threshold
  // snaps fully open. Swiping left on the drawer itself closes it.
  const [swipeOffset, setSwipeOffset] = useState(0);
  const [isSwiping, setIsSwiping] = useState(false);
  const swipingRef = useRef(false);
  const DRAWER_WIDTH = 280;
  const OPEN_THRESHOLD = 70;

  const edgeSwipe = useSwipeable({
    onSwiping: (e) => {
      if (drawerOpen) return;
      if (e.initial[0] > 30) return;
      if (e.dir !== 'Right' && !swipingRef.current) return;
      swipingRef.current = true;
      setIsSwiping(true);
      const offset = Math.min(Math.max(0, e.deltaX), DRAWER_WIDTH);
      setSwipeOffset(offset);
    },
    onSwiped: () => {
      if (!swipingRef.current) return;
      swipingRef.current = false;
      setIsSwiping(false);
      if (swipeOffset > OPEN_THRESHOLD) {
        setDrawerOpen(true);
      }
      setSwipeOffset(0);
    },
    trackMouse: false,
    delta: 10,
    preventScrollOnSwipe: false,
  });

  const drawerSwipe = useSwipeable({
    onSwipedLeft: () => {
      if (drawerOpen) setDrawerOpen(false);
    },
    trackMouse: false,
    delta: 40,
    preventScrollOnSwipe: true,
  });

  const partialOffset = isSwiping ? swipeOffset : 0;

  return (
    <div className="min-h-screen bg-[#FBFAF7] text-zinc-900">
      {/* ─── Desktop sidebar ─────────────────────────────── */}
      <aside className="hidden lg:flex lg:fixed lg:inset-y-0 lg:left-0 lg:w-[280px] lg:flex-col lg:border-r lg:border-zinc-200/70 lg:bg-white">
        <SidebarContent
          items={items}
          loyalty={loyalty}
          isHome={isHome}
          activeSlug={activeSlug}
          onNavHome={onNavHome}
          onNavItem={onNavItem}
          onNavLoyalty={onNavLoyalty}
        />
      </aside>

      {/* ─── Mobile header ───────────────────────────────
           On overview: no top bar — hero goes edge-to-edge with a floating
           pill burger that stays visible over photos and scrolled content.
           On section pages: sticky top bar with the section title. */}
      {isHome ? (
        <button
          onClick={() => setDrawerOpen(true)}
          className="lg:hidden fixed top-3 left-3 z-30 p-2.5 rounded-full bg-black/35 backdrop-blur-md text-white hover:bg-black/45 active:bg-black/55 transition shadow-sm"
          aria-label={t('Open menu')}
        >
          <Menu className="w-5 h-5" strokeWidth={2.25} />
        </button>
      ) : (
        <header className="lg:hidden sticky top-0 z-30 flex items-center justify-between px-4 py-3 bg-[#FBFAF7]/85 backdrop-blur-md border-b border-zinc-200/60">
          <button
            onClick={() => setDrawerOpen(true)}
            className="-ml-2 p-2 rounded-lg text-zinc-700 hover:bg-zinc-100 active:bg-zinc-200 transition"
            aria-label={t('Open menu')}
          >
            <Menu className="w-5 h-5" />
          </button>
          <div className="text-sm font-medium text-zinc-900 truncate max-w-[60%]">{mobileTitle}</div>
          <div className="w-9" />
        </header>
      )}

      {/* ─── Edge-grab strip for swipe-to-open (mobile) ─── */}
      {!drawerOpen && (
        <div
          {...edgeSwipe}
          className="lg:hidden fixed inset-y-0 left-0 w-6 z-20"
          style={{ touchAction: 'pan-y' }}
          aria-hidden="true"
        />
      )}

      {/* ─── Partial drawer preview during swipe ─── */}
      {!drawerOpen && partialOffset > 0 && (
        <div className="lg:hidden fixed inset-0 z-40 pointer-events-none">
          <div
            className="absolute inset-0 bg-zinc-900/30"
            style={{ opacity: Math.min(partialOffset / DRAWER_WIDTH, 0.6) }}
          />
          <div
            className="absolute inset-y-0 left-0 w-[280px] bg-white shadow-xl flex flex-col"
            style={{ transform: `translateX(${partialOffset - DRAWER_WIDTH}px)` }}
          >
            <SidebarContent
              items={items}
              loyalty={loyalty}
              isHome={isHome}
              activeSlug={activeSlug}
              onNavHome={onNavHome}
              onNavItem={onNavItem}
              onNavLoyalty={onNavLoyalty}
            />
          </div>
        </div>
      )}

      {/* ─── Mobile drawer ───────────────────────────────── */}
      {drawerOpen && (
        <div className="lg:hidden fixed inset-0 z-40" role="dialog" aria-modal="true">
          <div className="absolute inset-0 bg-zinc-900/30 backdrop-blur-sm" onClick={() => setDrawerOpen(false)} />
          <div
            {...drawerSwipe}
            className="absolute inset-y-0 left-0 w-[280px] bg-white shadow-xl flex flex-col animate-slide-in"
          >
            <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-200/70">
              <span className="text-sm font-medium text-zinc-900">{t('Menu')}</span>
              <button
                onClick={() => setDrawerOpen(false)}
                className="-mr-2 p-2 rounded-lg text-zinc-600 hover:bg-zinc-100"
                aria-label={t('Close menu')}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <SidebarContent
              items={items}
              loyalty={loyalty}
              isHome={isHome}
              activeSlug={activeSlug}
              onNavHome={onNavHome}
              onNavItem={onNavItem}
              onNavLoyalty={onNavLoyalty}
            />
          </div>
        </div>
      )}

      {/* ─── Main ────────────────────────────────────────── */}
      <main className="lg:pl-[280px] min-h-screen">{children}</main>

      <style>{`
        @keyframes slideIn { from { transform: translateX(-100%); } to { transform: translateX(0); } }
        .animate-slide-in { animation: slideIn 220ms cubic-bezier(0.22, 1, 0.36, 1); }
      `}</style>
    </div>
  );
}

function SidebarContent(props: {
  items: PortalItem[];
  loyalty: LoyaltyOutletState;
  isHome: boolean;
  activeSlug: string;
  onNavHome: () => void;
  onNavItem: (key: PortalItemKey) => void;
  onNavLoyalty: () => void;
}) {
  const { t } = useTranslation('portal');
  const { items, loyalty, isHome, activeSlug, onNavHome, onNavItem, onNavLoyalty } = props;
  const { summary } = useReservation();
  const showLoyaltyNav = loyalty.program !== null;
  const loyaltyEnrolled = loyalty.enrolledMembership !== null;

  const arrival = parseLocalDate(summary?.arrivalDate);
  const departure = parseLocalDate(summary?.departureDate);
  const nights = arrival && departure ? computeNights(arrival, departure) : null;

  return (
    <div className="flex flex-col h-full overflow-y-auto">
      {/* Property block — restrained: small thumb + clean type */}
      <div className="px-5 pt-6 pb-5 border-b border-zinc-100">
        <div className="flex items-start gap-3">
          {summary?.listingAvatarUrl ? (
            <img src={summary.listingAvatarUrl} alt="" className="w-12 h-12 rounded-lg object-cover bg-zinc-100" />
          ) : (
            <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-zinc-100 to-zinc-200" />
          )}
          <div className="min-w-0 flex-1">
            <div className="text-[13px] font-medium text-zinc-900 leading-tight line-clamp-2">
              {summary?.listingName || t('Your stay')}
            </div>
            {arrival && departure && (
              <div className="mt-1 text-[11px] text-zinc-500 tabular-nums">
                {formatCompactDate(arrival)} – {formatCompactDate(departure)}
                {nights && (
                  <>
                    {' · '}
                    {nights === 1 ? t('{{count}} night', { count: nights }) : t('{{count}} nights', { count: nights })}
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Nav */}
      <nav className="flex-1 px-2.5 py-4">
        <NavButton active={isHome} onClick={onNavHome} icon={Home} label={t('Overview')} />

        {(items.length > 0 || showLoyaltyNav) && (
          <>
            <div className="px-3 pt-5 pb-2 text-[10px] font-semibold uppercase tracking-[0.12em] text-zinc-400">
              {t('Your stay')}
            </div>
            {items.map((item) => {
              const Icon = ICON_FOR_KEY[item.key];
              const slug = SLUG_FOR_KEY[item.key];
              return (
                <NavButton
                  key={item.key}
                  active={activeSlug === slug}
                  onClick={() => onNavItem(item.key)}
                  icon={Icon}
                  label={item.title}
                  done={item.status === 'completed'}
                />
              );
            })}
            {showLoyaltyNav && loyalty.program && (
              <NavButton
                active={activeSlug === LOYALTY_SLUG}
                onClick={onNavLoyalty}
                icon={Gift}
                label={t('Loyalty program')}
                memberBadge={loyaltyEnrolled}
              />
            )}
          </>
        )}
      </nav>

      <div className="px-4 py-3 border-t border-zinc-100">
        <LanguageSelector placement="top-start" />
      </div>
    </div>
  );
}

function NavButton(props: {
  icon: typeof BookOpen;
  label: string;
  active?: boolean;
  done?: boolean;
  memberBadge?: boolean;
  onClick: () => void;
}) {
  const { t } = useTranslation('portal');
  const { icon: Icon, label, active, done, memberBadge, onClick } = props;
  return (
    <button
      onClick={onClick}
      className={`relative w-full flex items-center gap-3 px-3 py-2 rounded-lg text-[13.5px] transition ${
        active ? 'bg-zinc-100/80 text-zinc-900 font-medium' : 'text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900'
      }`}
    >
      {active && <span className="absolute left-0 top-2 bottom-2 w-[3px] rounded-r-full bg-[#1E3A5F]" />}
      <Icon className={`w-[17px] h-[17px] flex-shrink-0 ${active ? 'text-[#1E3A5F]' : 'text-zinc-400'}`} />
      <span className="truncate flex-1 text-left">{label}</span>
      {done && <span className="text-[10px] font-medium text-emerald-600 uppercase tracking-wider">{t('Done')}</span>}
      {memberBadge && (
        <span className="text-[10px] font-medium text-amber-600 uppercase tracking-wider">{t('Member')}</span>
      )}
    </button>
  );
}
